import * as Yup from "yup";

/**
 * @returns {Yup.Schema}
 */
export const getSecretKeySchema = () =>
  Yup.object().shape({
    secretKey: Yup.string()
      .required("Please enter a secret key")
      .min(8, "Secret key must be minimum 8 characters")
      .matches(
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[ !"#$%&'()*+,./:;<=>?@\\[\]^_`{|}~-]).{8,64}$/,
        "Secret key must contain at least 1 upper case, 1 lower case letter, 1 digit and 1 special symbol",
      ),
  });

/**
 * @param {Object} options
 * @param {Object} options.limitData
 * @param {Number} options.limitData.khrNbcLimit
 * @param {Number} options.limitData.usdNbcLimit
 * @param {Number} options.limitData.localLimit
 * @returns {Yup.Schema}
 */
export const getLimitSchema = ({
  limitData = {
    khrNbcLimit: Infinity,
    usdNbcLimit: Infinity,
    customLimit: true,
  },
} = {}) => {
  return Yup.object().shape({
    KHR: Yup.number()
      .required("Please enter KHR limit")
      .min(0, "KHR limit can not be less than 0")
      .max(
        limitData.customLimit ? 999999999999999 : limitData.khrNbcLimit,
        "KHR limit can not be greater than limit by NBC",
      ),
    USD: Yup.number()
      .required("Please enter USD limit")
      .min(0, "USD limit can not be less than 0")
      .max(
        limitData.customLimit ? 999999999999999 : limitData.usdNbcLimit,
        "USD limit can not be greater than limit by NBC",
      ),
    limitName: Yup.string().required("Please enter limit name"),
  });
};

/**
 * @returns {Yup.Schema}
 */
export const getNewLimitSchema = () =>
  Yup.object().shape({
    KHR: Yup.number()
      .required("Please enter KHR limit")
      .min(0, "KHR limit can not be less than 0")
      .max(
        999999999999999,
        "KHR limit can not be greater than 999999999999999",
      ),
    USD: Yup.number()
      .required("Please enter USD limit")
      .min(0, "USD limit can not be less than 0")
      .max(
        999999999999999,
        "USD limit can not be greater than 999999999999999",
      ),
    limitName: Yup.string().required("Please enter name for the limit"),
    accountType: Yup.string().required(
      "Please select account type for the limit",
    ),
  });

/**
 * Checks if given value are valid account ID
 * @param {String} string
 * @returns {Boolean}
 */
export function isAccountId(string) {
  return /^[a-z_0-9]{1,32}@[a-z_0-9]{1,9}$/i.test(string);
}

/**
 * Checks if given value are valid phone number
 * @param {String} string
 * @returns {Boolean}
 */
export function isPhoneNumber(string) {
  const clearedNumber = clearPhoneNumber(string);
  return /^855\d+$/.test(clearedNumber) && isRightPhoneLength(clearedNumber);
}

/**
 * Checks if given phone number has valid length
 * @param {String} string
 * @returns {Boolean}
 */
export function isRightPhoneLength(string) {
  return string.length > 10 && string.length < 13;
}
/**
 * A little helper to clear cambodian phone number string
 * @param {String} phoneNumber
 * @returns {String}
 */
export function clearPhoneNumber(phoneNumber) {
  function clearCountryCodeRegex(string) {
    return string.replace(/^(\+? ?855|\+? ?\(855\)|\+? ?0)/, "");
  }

  function clearSpacesAndHyphens(string) {
    return string.replace(/( |-)/g, "");
  }

  return "855" + clearSpacesAndHyphens(clearCountryCodeRegex(phoneNumber));
}

/**
 * @param {Object} options
 * @param {String} options.selectedCurrency `"KHR"` or `"USD"`
 * @param {Number} options.balance amount of the current balance
 * @returns {Yup.Schema}
 */
export const getTransferSchema = ({ selectedCurrency, balance }) =>
  Yup.object().shape({
    transferTo: Yup.string()
      .required("Please enter account ID or phone number")
      .ensure()
      .lowercase()
      .test(
        "transferToValidation",
        "The value entered is not recognized as a valid account ID or phone number",
        value => {
          if (isAccountId(value)) {
            return true;
          }

          if (isPhoneNumber(value) && value[5] !== "0") {
            return true;
          }

          return false;
        },
      ),
    amount: Yup.number()
      .test(
        "khrFraction",
        "KHR amount should not contain any fraction numbers",
        amount => {
          const fraction = String(amount).split(".")[1];
          if (selectedCurrency === "KHR" && fraction) {
            return false;
          }

          return true;
        },
      )
      .test(
        "usdFraction",
        "USD amount should not contain fraction with more than 2 numbers",
        amount => {
          const fraction = String(amount).split(".")[1];
          if (selectedCurrency === "USD" && fraction && fraction.length > 2) {
            return false;
          }

          return true;
        },
      )
      .max(
        balance,
        "The current amount exceeds available balance on the main account",
      )
      .required("Please enter an amount")
      .positive("Amount must be positive number"),
    description: Yup.string()
      .test(
        "doesNotExceed64bytes",
        "Please make sure your description doesn't exceed 64 bytes length",
        description => {
          const bytes = new Blob([description]).size;

          return bytes <= 64;
        },
      )
      .max(64, "Description length must be less than 64 symbols"),
    fromName: Yup.string().when("fromPhone", {
      is: phone => phone && phone.length > 0,
      then: Yup.string().required("Please enter name"),
    }),
    fromPhone: Yup.string().test(
      "validPhoneNumber",
      "Please enter valid phone number",
      phone => {
        if ((phone && isPhoneNumber(phone) && phone[5] !== "0") || !phone) {
          return true;
        }

        return false;
      },
    ),
  });

export const facebookRegExp = /(?:(?:http|https):\/\/)?(?:www.)?(facebook|fb)\.com\/(?:(?:\w)*#!\/)?(?:pages\/)?(?:[?\w\-]*\/)?(?:profile.php\?id=(?=\d.*))?([\w\-]*)?/;

export const trimField = e => {
  e.target.value = e.target.value.trim();
  return e;
};
